import { useEffect, useMemo, useState } from "react";
import useApi from "@/hooks/useApi";
import useAIConfigs from "@/hooks/useAIConfigs";
import useChatWindows from "@/hooks/useChatWindows";
import useWorkflows from "@/hooks/useWorkflows";
import useSkillCatalog from "@/hooks/useSkillCatalog";
import type { TourFacts } from "./tourSteps";

/**
 * Builds the live facts the Shopify welcome tour narrates.
 *
 * Every field here comes from a real API response. Nothing is invented, estimated, or
 * defaulted to a plausible-looking number: a field that couldn't be loaded stays `null`, and
 * tourSteps.ts renders no sentence for a null fact (the step still deep-links to the real page).
 *
 * Most of it costs no extra requests — PrivateRoute already mounts AIConfigsProvider,
 * ChatWindowsProvider and WorkflowsProvider above every page, and useSkillCatalog has its own
 * 60s module-level cache. Only the Shopify config list and the default Chat Experience bundle
 * are fetched here, and only while the tour is actually open (`enabled`).
 */

/** Name SiteProvisioningService.SeedDemoBot gives the auto-created storefront assistant. */
const PROVISIONED_ASSISTANT_NAME = "Store Assistant (Setting Up)";

interface ShopifyConfigRow {
  id: number;
  storeName?: string;
  domain?: string;
  widgetDeployed?: boolean;
}

export default function useShopifyTourFacts(enabled: boolean): {
  facts: TourFacts;
  loading: boolean;
} {
  const api = useApi();
  const { aiConfigurations } = useAIConfigs();
  const { chatWindows } = useChatWindows();
  const { workflows } = useWorkflows();
  const { skills, loading: skillsLoading, error: skillsError } = useSkillCatalog();

  const [shopifyConfig, setShopifyConfig] = useState<ShopifyConfigRow | null>(null);
  const [hasPreChatSurvey, setHasPreChatSurvey] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);

    async function load() {
      try {
        const resp = await api.messaging.get("Shopify/Integrations/");
        if (resp.ok) {
          const data = await resp.json();
          if (!cancelled && Array.isArray(data) && data.length > 0) {
            setShopifyConfig(data[0] as ShopifyConfigRow);
          }
        }
      } catch {
        // A failed fetch leaves the facts null, which renders as "no sentence" rather than a
        // wrong sentence. Deliberately not surfaced as a page-level error: the tour is
        // supplementary, and a broken stat must never block the deep link that still works.
        if (!cancelled) setShopifyConfig(null);
      }

      try {
        const resp = await api.messaging.get("Bundle/default");
        if (resp.ok) {
          const bundle = await resp.json();
          if (!cancelled && bundle && typeof bundle === "object") {
            setHasPreChatSurvey(
              bundle.preChatSurveyId !== null && bundle.preChatSurveyId !== undefined
            );
          }
        } else if (resp.status === 404 && !cancelled) {
          // A site with no bundles yet genuinely has no pre-chat survey — that's a real fact,
          // not a failure.
          setHasPreChatSurvey(false);
        }
      } catch {
        if (!cancelled) setHasPreChatSurvey(null);
      }

      if (!cancelled) setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [enabled, api]);

  const facts = useMemo<TourFacts>(() => {
    // The catalog is the server's authority on both the skill SET and this site's entitlement
    // (`included`). If it failed to load we report nothing rather than a misleading zero.
    const catalogUsable = !skillsLoading && !skillsError;
    const shopifySkills = catalogUsable
      ? skills.filter((s) => s.name.startsWith("shopify_"))
      : [];

    // Prefer the assistant auto-provisioning actually created. Falling back to "the only one"
    // is safe; guessing among several is not, so that case reports nothing.
    const provisioned = aiConfigurations.find(
      (c) => c.name === PROVISIONED_ASSISTANT_NAME
    );
    const assistant =
      provisioned ?? (aiConfigurations.length === 1 ? aiConfigurations[0] : null);

    const openingWorkflow =
      workflows.find((w) => w.triggerType === "conversation_opened") ?? null;

    return {
      storeDomain: shopifyConfig?.domain ?? null,
      storeName: shopifyConfig?.storeName || null,
      widgetDeployed: shopifyConfig ? shopifyConfig.widgetDeployed === true : null,
      activeShopifySkillCount: catalogUsable
        ? shopifySkills.filter((s) => s.included).length
        : null,
      totalShopifySkillCount: catalogUsable ? shopifySkills.length : null,
      chatWindowCount: chatWindows.length,
      assistantName: assistant?.name ?? null,
      assistantId: assistant?.id ?? null,
      workflowName: openingWorkflow?.name ?? null,
      workflowId: openingWorkflow?.id ?? null,
      workflowEnabled: openingWorkflow ? openingWorkflow.enabled === true : null,
      hasPreChatSurvey
    };
  }, [
    skills,
    skillsLoading,
    skillsError,
    aiConfigurations,
    chatWindows,
    workflows,
    shopifyConfig,
    hasPreChatSurvey
  ]);

  return { facts, loading };
}
